import { NetworkStatus } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import {
  rightDrawerTablePaginationMeasure,
  rightDrawerTableSpinnerMeasure,
} from 'domains/reporter/RichTextEditor/analytics/performance';

import type { RightDrawerTabType } from './types';
import type { TOwnerScope } from './useOwnerScope';
import type { TStudyRelevance } from './useStudyRelevance';

export const RIGHT_DRAWER_TABLE_LOADING_LONG_THRESHOLD_MS = 5000;

type RightDrawerTableLoadingTelemetryPayloadProps = {
  activeTab: RightDrawerTabType;
  cursor: Nullable<string>;
  dataItemsLength: Nullable<number>;
  itemsLength: number;
  networkStatus: NetworkStatus;
  order: string;
  ownerScope: TOwnerScope;
  previousDataItemsLength: Nullable<number>;
  search: string;
  studyRelevance: TStudyRelevance;
};

type RightDrawerTableLoadingTelemetryPayload = {
  activeTab: RightDrawerTabType;
  dataItemsLength: Nullable<number>;
  hasCursor: boolean;
  itemsLength: number;
  networkStatusName: string;
  order: string;
  ownerScope: TOwnerScope;
  previousDataItemsLength: Nullable<number>;
  searchLength: number;
  studyRelevance: TStudyRelevance;
};

type UseRightDrawerTableSpinnerTelemetryProps = RightDrawerTableLoadingTelemetryPayloadProps & {
  isLoading: boolean;
};

const NETWORK_STATUS_NAMES: Partial<Record<NetworkStatus, string>> = {
  [NetworkStatus.loading]: 'loading',
  [NetworkStatus.setVariables]: 'setVariables',
  [NetworkStatus.fetchMore]: 'fetchMore',
  [NetworkStatus.refetch]: 'refetch',
  [NetworkStatus.poll]: 'poll',
  [NetworkStatus.ready]: 'ready',
  [NetworkStatus.error]: 'error',
};

const getNetworkStatusName = (networkStatus: NetworkStatus): string => {
  return NETWORK_STATUS_NAMES[networkStatus] ?? 'unknown';
};

const useRightDrawerTableLoadingTelemetryPayload = ({
  activeTab,
  cursor,
  dataItemsLength,
  itemsLength,
  networkStatus,
  order,
  ownerScope,
  previousDataItemsLength,
  search,
  studyRelevance,
}: RightDrawerTableLoadingTelemetryPayloadProps) => {
  const telemetryPayload = useMemo<RightDrawerTableLoadingTelemetryPayload>(
    () => ({
      activeTab,
      dataItemsLength,
      hasCursor: cursor != null,
      itemsLength,
      networkStatusName: getNetworkStatusName(networkStatus),
      order,
      ownerScope,
      previousDataItemsLength,
      searchLength: search.length,
      studyRelevance,
    }),
    [
      activeTab,
      cursor,
      dataItemsLength,
      itemsLength,
      networkStatus,
      order,
      ownerScope,
      previousDataItemsLength,
      search,
      studyRelevance,
    ]
  );

  const latestTelemetryPayloadRef = useRef(telemetryPayload);
  useEffect(() => {
    latestTelemetryPayloadRef.current = telemetryPayload;
  }, [telemetryPayload]);

  return latestTelemetryPayloadRef;
};

export const useRightDrawerTableSpinnerTelemetry = ({
  activeTab,
  cursor,
  dataItemsLength,
  isLoading,
  itemsLength,
  networkStatus,
  order,
  ownerScope,
  previousDataItemsLength,
  search,
  studyRelevance,
}: UseRightDrawerTableSpinnerTelemetryProps): void => {
  const spinnerStartedAtRef = useRef<Nullable<number>>(null);
  const thresholdTimerRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const thresholdReportedRef = useRef(false);
  const thresholdTelemetryPayloadRef =
    useRef<Nullable<RightDrawerTableLoadingTelemetryPayload>>(null);
  const latestTelemetryPayloadRef = useRightDrawerTableLoadingTelemetryPayload({
    activeTab,
    cursor,
    dataItemsLength,
    itemsLength,
    networkStatus,
    order,
    ownerScope,
    previousDataItemsLength,
    search,
    studyRelevance,
  });

  // The resolved phase is measured by the User Timing observer; only the fixed 5s threshold phase
  // still reports its own duration.
  const trackSpinnerThresholdEvent = useCallback(
    (durationMs: number, payload: RightDrawerTableLoadingTelemetryPayload) => {
      analytics.track(reporter.sys.rightDrawerTableSpinnerLong, {
        ...payload,
        duration_ms: durationMs,
        phase: 'threshold',
      });
    },
    []
  );

  const finishSpinnerTracking = useCallback(() => {
    if (thresholdTimerRef.current != null) {
      clearTimeout(thresholdTimerRef.current);
      thresholdTimerRef.current = null;
    }

    if (spinnerStartedAtRef.current != null) {
      if (thresholdReportedRef.current) {
        rightDrawerTableSpinnerMeasure.measure({
          ...thresholdTelemetryPayloadRef.current!,
          phase: 'resolved',
        });
      } else {
        // Most episodes finish under the threshold: drop the mark without emitting.
        rightDrawerTableSpinnerMeasure.cancel();
      }
    }

    spinnerStartedAtRef.current = null;
    thresholdReportedRef.current = false;
    thresholdTelemetryPayloadRef.current = null;
  }, []);

  // Treat only the full table overlay as blocking; rows with data use a non-blocking progress bar.
  const blockingSpinnerVisible = isLoading && itemsLength === 0;

  useEffect(() => {
    if (!blockingSpinnerVisible) {
      finishSpinnerTracking();
      return;
    }

    if (spinnerStartedAtRef.current != null) {
      return;
    }

    spinnerStartedAtRef.current = Date.now();
    rightDrawerTableSpinnerMeasure.start();
    thresholdReportedRef.current = false;
    thresholdTimerRef.current = setTimeout(() => {
      if (spinnerStartedAtRef.current == null) {
        return;
      }

      thresholdReportedRef.current = true;
      thresholdTimerRef.current = null;
      thresholdTelemetryPayloadRef.current = latestTelemetryPayloadRef.current;
      trackSpinnerThresholdEvent(
        Date.now() - spinnerStartedAtRef.current,
        thresholdTelemetryPayloadRef.current
      );
    }, RIGHT_DRAWER_TABLE_LOADING_LONG_THRESHOLD_MS);

    return () => {
      if (thresholdTimerRef.current != null) {
        clearTimeout(thresholdTimerRef.current);
        thresholdTimerRef.current = null;
      }
    };
  }, [
    blockingSpinnerVisible,
    finishSpinnerTracking,
    latestTelemetryPayloadRef,
    trackSpinnerThresholdEvent,
  ]);

  useEffect(() => {
    return finishSpinnerTracking;
  }, [finishSpinnerTracking]);
};

export const useRightDrawerTablePaginationTelemetry = ({
  activeTab,
  cursor,
  dataItemsLength,
  itemsLength,
  networkStatus,
  order,
  ownerScope,
  previousDataItemsLength,
  search,
  studyRelevance,
}: RightDrawerTableLoadingTelemetryPayloadProps): ((request: Promise<unknown>) => void) => {
  const paginationStartedAtRef = useRef<Nullable<number>>(null);
  const thresholdTimerRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const thresholdReportedRef = useRef(false);
  const thresholdTelemetryPayloadRef =
    useRef<Nullable<RightDrawerTableLoadingTelemetryPayload>>(null);
  const latestTelemetryPayloadRef = useRightDrawerTableLoadingTelemetryPayload({
    activeTab,
    cursor,
    dataItemsLength,
    itemsLength,
    networkStatus,
    order,
    ownerScope,
    previousDataItemsLength,
    search,
    studyRelevance,
  });

  // The resolved phase is measured by the User Timing observer; only the fixed 5s threshold phase
  // still reports its own duration.
  const trackPaginationThresholdEvent = useCallback(
    (durationMs: number, payload: RightDrawerTableLoadingTelemetryPayload) => {
      analytics.track(reporter.sys.rightDrawerTablePaginationLong, {
        ...payload,
        duration_ms: durationMs,
        phase: 'pagination_threshold',
      });
    },
    []
  );

  const finishPaginationTracking = useCallback(() => {
    if (thresholdTimerRef.current != null) {
      clearTimeout(thresholdTimerRef.current);
      thresholdTimerRef.current = null;
    }

    if (paginationStartedAtRef.current != null) {
      if (thresholdReportedRef.current) {
        rightDrawerTablePaginationMeasure.measure({
          ...thresholdTelemetryPayloadRef.current!,
          phase: 'pagination_resolved',
        });
      } else {
        // Most episodes finish under the threshold: drop the mark without emitting.
        rightDrawerTablePaginationMeasure.cancel();
      }
    }

    paginationStartedAtRef.current = null;
    thresholdReportedRef.current = false;
    thresholdTelemetryPayloadRef.current = null;
  }, []);

  useEffect(() => {
    return finishPaginationTracking;
  }, [finishPaginationTracking]);

  return useCallback(
    (request: Promise<unknown>) => {
      if (paginationStartedAtRef.current != null) {
        // Ignore duplicate calls defensively; onLoadMore already gates on Apollo loading.
        return;
      }

      paginationStartedAtRef.current = Date.now();
      rightDrawerTablePaginationMeasure.start();
      thresholdReportedRef.current = false;
      thresholdTimerRef.current = setTimeout(() => {
        if (paginationStartedAtRef.current == null) {
          return;
        }

        thresholdReportedRef.current = true;
        thresholdTimerRef.current = null;
        thresholdTelemetryPayloadRef.current = {
          ...latestTelemetryPayloadRef.current,
          networkStatusName: 'fetchMore',
        };
        trackPaginationThresholdEvent(
          Date.now() - paginationStartedAtRef.current,
          thresholdTelemetryPayloadRef.current
        );
      }, RIGHT_DRAWER_TABLE_LOADING_LONG_THRESHOLD_MS);

      request.then(finishPaginationTracking, finishPaginationTracking);
    },
    [finishPaginationTracking, latestTelemetryPayloadRef, trackPaginationThresholdEvent]
  );
};
